import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import { organisationApi } from '../../api/endpoints.js';
import type { Branch } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,

  DataTable,
  Dialog,
  Form,
  FormSection,
  PageHeader,
  StatusBadge,
  SwitchField,
  TextAreaField,
  TextField,
  useToast,
  type DataColumn,
  SaveErrorNotice,
} from '../../components/ui/index.js';
import { optionalText } from '../clients/bilingual.js';
import { RegisterPanel } from '../reports/register-panel.js';

/**
 * The laboratory's own branches.
 *
 * ===========================================================================
 * NOTHING ON THIS SCREEN SHIPS WITH THE PRODUCT.
 * ===========================================================================
 *
 * A branch name, its code, its address and its telephone number belong to one
 * laboratory. They are rows it creates here — never a list written into the
 * source, and deliberately not even a seeded "Main Branch", which would be
 * this product asserting an identity that belongs to a customer
 * (CLAUDE.md section 6.1, decision D53). An empty list on a new laboratory's
 * first day is therefore correct, not a gap to be filled in for them.
 *
 * A BRANCH IS DEACTIVATED, NEVER DELETED. Work was carried out at one, and a
 * work order that names it must still be able to say where it was done.
 */
export function BranchesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { show } = useToast();

  const canManage = can(PERMISSIONS.BRANCHES_MANAGE);

  const [editing, setEditing] = useState<Branch | null>(null);
  const [isCreating, setCreating] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [addressLine, setAddressLine] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [isActive, setActive] = useState(true);
  const [reason, setReason] = useState('');

  const branches = useQuery({
    queryKey: ['branches', 'all'],
    queryFn: () => organisationApi.listBranches(true),
  });

  const isOpen = isCreating || editing !== null;

  const openCreate = () => {
    setEditing(null);
    setCode('');
    setName('');
    setAddressLine('');
    setContactEmail('');
    setContactPhone('');
    setNotes('');
    setActive(true);
    setReason('');
    save.reset();
    setCreating(true);
  };

  const openEdit = (branch: Branch) => {
    setCreating(false);
    setCode(branch.code ?? '');
    setName(branch.name);
    setAddressLine(branch.addressLine ?? '');
    setContactEmail(branch.contactEmail ?? '');
    setContactPhone(branch.contactPhone ?? '');
    setNotes(branch.notes ?? '');
    setActive(branch.isActive);
    setReason('');
    save.reset();
    setEditing(branch);
  };

  const close = () => {
    setCreating(false);
    setEditing(null);
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: optionalText(code),
        name: name.trim(),
        addressLine: optionalText(addressLine),
        contactEmail: optionalText(contactEmail),
        contactPhone: optionalText(contactPhone),
        notes: optionalText(notes),
      };
      if (editing) {
        return organisationApi.updateBranch(editing.id, {
          ...body,
          isActive,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        });
      }
      return organisationApi.createBranch(body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['branches'] });
      close();
      // Raised from the server's confirmation, never from the button press.
      show({ tone: 'success', message: t('organisation.branches.saved') });
    },
  });

  const columns: readonly DataColumn<Branch>[] = [
    {
      key: 'name',
      header: t('organisation.branches.name'),
      render: (branch) => (
        <div className="min-w-0">
          <span className="block truncate text-default-900">
            {branch.name ?? t('common.notRecorded')}
          </span>
          {branch.code ? (
            <span className="text-caption text-default-600" dir="ltr">
              {branch.code}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'address',
      header: t('organisation.branches.addressLine'),
      render: (branch) => (
        <span className="text-default-600">
          {branch.addressLine ?? t('common.notRecorded')}
        </span>
      ),
    },
    {
      key: 'contact',
      header: t('organisation.branches.contact'),
      render: (branch) => (
        <div className="min-w-0">
          <span className="block truncate text-caption text-default-600" dir="ltr">
            {branch.contactEmail ?? t('common.notRecorded')}
          </span>
          <span className="block text-caption text-default-600" dir="ltr">
            {branch.contactPhone ?? ''}
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (branch) => (
        <StatusBadge
          label={branch.isActive ? t('common.active') : t('common.inactive')}
          tone={branch.isActive ? 'success' : 'neutral'}
        />
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (branch) =>
        canManage ? (
          <ActionButton
            priority="tertiary"
            size="sm"
            onPress={() => openEdit(branch)}
          >
            {t('common.edit')}
          </ActionButton>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title={t('organisation.branches.title')}
        subtitle={t('organisation.branches.subtitle')}
        breadcrumbs={[
          { label: t('nav.settings'), to: '/settings' },
          { label: t('organisation.branches.title') },
        ]}
        actions={
          canManage ? (
            <ActionButton priority="primary" onPress={openCreate}>
              {t('organisation.branches.create')}
            </ActionButton>
          ) : null
        }
      />

      <RegisterPanel>
        <DataTable
          label={t('organisation.branches.title')}
          columns={columns}
          rows={branches.data?.data ?? []}
          rowKey={(branch) => branch.id}
          isLoading={branches.isLoading}
          error={branches.error}
          onRetry={() => void branches.refetch()}
          emptyContent={t('organisation.branches.noneYet')}
        />
      </RegisterPanel>

      <Dialog
        isOpen={isOpen}
        onClose={close}
        title={
          editing
            ? t('organisation.branches.editTitle')
            : t('organisation.branches.create')
        }
        size="lg"
      >
        {editing && !isActive ? (
          <div className="mb-stack">
            <Alert
              tone="warning"
              title={t('organisation.branches.deactivateNotice')}
              description={t('organisation.branches.deactivateHint')}
            />
          </div>
        ) : null}

        <Form
          onSubmit={() => save.mutate()}
          isSubmitting={save.isPending}
          actions={
            <ActionBar>
              <ActionButton priority="tertiary" onPress={close}>
                {t('common.cancel')}
              </ActionButton>
              <ActionButton
                priority="primary"
                type="submit"
                isLoading={save.isPending}
              >
                {t('common.save')}
              </ActionButton>
            </ActionBar>
          }
        >
          <FormSection title={t('organisation.branches.title')} columns={1}>
            <TextField
              label={t('organisation.branches.name')}
              value={name}
              onChange={setName}
              textDirection="auto"
              isRequired
            />
            <TextField
              label={t('organisation.branches.address')}
              value={addressLine}
              onChange={setAddressLine}
              textDirection="auto"
            />
          </FormSection>

          <FormSection title={t('organisation.branches.contact')}>
            <TextField
              label={t('organisation.branches.code')}
              description={t('organisation.branches.codeHint')}
              value={code}
              onChange={setCode}
            />
            <TextField
              label={t('organisation.branches.contactEmail')}
              type="email"
              value={contactEmail}
              onChange={setContactEmail}
            />
            <TextField
              label={t('organisation.branches.contactPhone')}
              type="tel"
              value={contactPhone}
              onChange={setContactPhone}
            />
          </FormSection>

          <FormSection title={t('common.details')} columns={1}>
            <TextAreaField
              label={t('organisation.branches.notes')}
              value={notes}
              onChange={setNotes}
              rows={2}
            />
            {editing ? (
              <>
                <SwitchField
                  label={t('common.active')}
                  description={t('organisation.branches.activeHint')}
                  value={isActive}
                  onChange={setActive}
                />
                <TextAreaField
                  label={t('common.reasonOptional')}
                  value={reason}
                  onChange={setReason}
                  rows={2}
                />
              </>
            ) : null}
          </FormSection>

          {save.error ? <SaveErrorNotice error={save.error} fieldLabels={{ name: t('organisation.branches.name'), code: t('organisation.branches.code'), addressLine: t('organisation.branches.address'), contactEmail: t('organisation.branches.contactEmail'), contactPhone: t('organisation.branches.contactPhone') }} /> : null}
        </Form>
      </Dialog>
    </>
  );
}
